// src/pages/InboundBusinessPage.js
// 旅行手配インバウンド事業ページ
// 設計仕様: docs/superpowers/specs/2026-09-03-inbound-business-page-design.md
import React, { useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useLocalizedNav } from '../i18n/useLocalizedNav';
import { SUPPORTED_LANGS, LANG_NAMES } from '../i18n/config';
import { useDocumentMeta } from '../i18n/useDocumentMeta';
import { pick } from '../i18n/pick';
import {
  IMAGES, RING, SIDE_LABELS, CONTACT, SERVICE_KEYS, AREA_KEYS, FAQ_KEYS, CASES,
} from '../data/inboundBusinessData';
import useScrollReveal from '../hooks/useScrollReveal';
import useParallax from '../hooks/useParallax';
import useCountUp from '../hooks/useCountUp';
import useRingDecoration from '../hooks/useRingDecoration';
import useSideLabels from '../hooks/useSideLabels';

const STYLES = `
.ib{--ac:#2563eb;--ink:#191919;--wrapw:1100px;--sidelabel:#dfe2e6;
    position:relative;background:#eef1f5;overflow-x:hidden}
.ib *{box-sizing:border-box}
.ib img{display:block;max-width:100%}

/* ---- 背景リング（wrap外・PCのみ） ---- */
/* 絶対配置した円はそのままだとページ下端を押し広げ、フッター下に余白を作る。
   ページと同じ高さの箱でクリップして、はみ出しを切り落とす。 */
.ib-ringclip{position:absolute;left:0;right:0;top:0;bottom:0;overflow:hidden;z-index:1;pointer-events:none}
.ib-ringlayer{position:absolute;left:50%;width:0;height:0}
.ib-ring{position:absolute;left:0;top:0;width:0;height:0;will-change:transform}
.ring-slot{position:absolute;left:0;top:0;width:0;height:0}
.ring-frame{position:absolute;overflow:hidden;background:#c8d0dc;box-shadow:0 14px 38px rgba(20,35,60,.26)}
.ring-frame img{width:100%;height:100%;object-fit:cover}

/* ---- 縦ラベル ---- */
/* モックアップと同じ fixed。画面に貼り付いたまま動かない。
   sticky にすると .ib の overflow-x:hidden が効いてスクロールコンテナ扱いになり、
   viewport に対して固定されずそのまま流れ去ってしまう。 */
.ib-side{position:fixed;top:0;height:100vh;z-index:0;pointer-events:none;
  width:calc((100% - var(--wrapw)) / 2);
  display:flex;align-items:center;justify-content:center;
  writing-mode:vertical-rl;text-orientation:upright;line-height:1;
  font-family:Montserrat,'Segoe UI',sans-serif;font-weight:800;font-size:120px;letter-spacing:-.05em;
  color:var(--sidelabel);user-select:none}
.ib-side.l{left:0}
.ib-side.r{right:0}

@media (max-width:${RING.minViewport}px){
  .ib-ringclip,.ib-side{display:none!important}
}

/* ---- wrap ---- */
.ib-wrap{position:relative;z-index:2;max-width:var(--wrapw);margin:0 auto;background:#fff;
  box-shadow:0 0 60px rgba(20,35,60,.08)}

/* ---- ヘッダー（このページ専用。共通ヘッダーは LangLayout 側で出さない） ---- */
.ib-hd{background:#2b2b2b;display:flex;align-items:center;justify-content:space-between;
  height:56px;padding-left:24px;position:sticky;top:0;z-index:40}
.ib-hd .logo{display:flex;align-items:center;background:none;border:0;padding:0;cursor:pointer}
/* 濃色ヘッダー上でロゴを白抜きにする。白版の画像を用意できれば差し替える。 */
.ib-hd .logo img{height:30px;width:auto;filter:brightness(0) invert(1)}
.ib-hd nav{display:flex;align-items:center;gap:24px}
.ib-hd nav button{background:none;border:none;color:#fff;font-size:13.5px;font-weight:500;
  cursor:pointer;padding:0;font-family:inherit}
.ib-hd nav button:hover{color:#8fb4ff}
.ib-lang{position:relative;display:flex;align-items:center}
.ib-lang select{appearance:none;-webkit-appearance:none;background:transparent;
  border:1px solid rgba(255,255,255,.45);border-radius:20px;color:#fff;font-family:inherit;
  font-size:12px;font-weight:500;padding:6px 28px 6px 13px;cursor:pointer}
.ib-lang select option{color:#191919}
.ib-lang::after{content:'';position:absolute;right:11px;top:50%;margin-top:-2px;pointer-events:none;
  border-left:4px solid transparent;border-right:4px solid transparent;border-top:5px solid #fff}
.ib-hd .cta{background:var(--ac);color:#fff;font-weight:700;font-size:13px;height:56px;
  display:flex;align-items:center;padding:0 26px;text-decoration:none;white-space:nowrap}

.ib-hero{position:relative;height:440px;overflow:hidden;background:#33404f}
.ib-hero .bg{position:absolute;left:0;right:0;top:-60px;height:calc(100% + 120px);
  background:center/cover no-repeat;will-change:transform}
.ib-hero::after{content:'';position:absolute;inset:0;background:rgba(14,21,33,.42)}
.ib-hero .inner{position:absolute;inset:0;z-index:2}
.ib-crumb{position:absolute;top:18px;left:38px;right:38px;font-size:9.5px;color:rgba(255,255,255,.72);
  border-bottom:1px solid rgba(255,255,255,.26);padding-bottom:12px}
.ib-crumb span[aria-current]{color:#fff}
.ib-big{position:absolute;left:30px;top:96px;margin:0;font-family:Montserrat,'Segoe UI',sans-serif;
  font-size:clamp(38px,7vw,84px);font-weight:800;letter-spacing:-.028em;line-height:.92;
  color:var(--ac);will-change:transform}
.ib-cat{position:absolute;right:38px;bottom:112px;color:#fff;font-size:clamp(17px,2.4vw,25px);
  font-weight:700;line-height:1.6;text-shadow:0 2px 20px rgba(0,0,0,.5);will-change:transform}
.ib-anch{position:absolute;left:38px;bottom:30px;display:flex;gap:10px}
.ib-anch a{background:var(--ac);color:#fff;font-size:10.5px;font-weight:700;padding:12px 17px;
  text-decoration:none}
.ib-anch a.mail{background:#fff;color:var(--ink)}

.ib-s{padding:70px 46px;position:relative}
.ib-s.gray{background:#f6f7f9}
.ib-s.clear{background:transparent}
.ib-sh{margin-bottom:32px}
.ib-sh h2{font-size:22px;color:var(--ac);font-weight:700;margin:0 0 4px}
.ib-sh p{font-family:Montserrat,sans-serif;font-size:10px;font-weight:700;letter-spacing:.14em;
  color:#9aa1ab;margin:0}
.ib-split{display:grid;grid-template-columns:1fr 1fr;gap:44px;align-items:center}
.ib-h3{font-size:26px;font-weight:700;line-height:1.55;margin:0 0 16px}
.ib-h3 em{font-style:normal;color:var(--ac)}
.ib-p{font-size:12.5px;line-height:2.1;color:#4a4f57;margin:0}
.ib-ul{list-style:none;margin:22px 0 0;padding:0}
.ib-ul li{font-size:12px;color:#4a4f57;padding:11px 0 11px 20px;border-bottom:1px solid #e4e7ec;
  position:relative}
.ib-ul li::before{content:'';position:absolute;left:0;top:17px;width:9px;height:1.5px;background:var(--ac)}
.ib-ph{position:relative;overflow:hidden;aspect-ratio:4/3;background:#dfe3e8}
.ib-ph .img{position:absolute;left:0;right:0;top:-8%;height:116%;background:center/cover no-repeat;
  will-change:transform}

.ib-nums{background:var(--ac);color:#fff;padding:54px 46px;display:grid;grid-template-columns:1.32fr 1fr}
.ib-n{padding:0 36px}
.ib-n:first-child{padding-left:0;border-right:1px solid rgba(255,255,255,.32)}
.ib-nl{font-size:12.5px;font-weight:700;margin:0 0 10px}
.ib-nb{font-family:Montserrat,sans-serif;font-size:clamp(32px,4vw,52px);font-weight:800;
  letter-spacing:-.03em;line-height:1;margin:0}
.ib-nb small{font-size:20px;font-weight:700}
.ib-nn{font-size:10px;color:rgba(255,255,255,.84);margin:12px 0 0;line-height:1.8}

.ib-areasec .ib-sh h2{font-size:32px;letter-spacing:-.01em}
.ib-areasec .ib-sh p{font-size:11px;letter-spacing:.2em}
.ib-areasec .lead{font-size:15px;line-height:2.15;color:#3d434b;margin:0}
.ib-areas{display:flex;justify-content:center;flex-wrap:wrap;margin-top:52px}
.ib-ar{padding:0 36px;text-align:center;position:relative;min-width:212px}
.ib-ar+.ib-ar::before{content:'';position:absolute;left:0;top:10px;bottom:10px;width:1px;background:#dde2e9}
.ib-ar b{display:block;font-size:28px;color:var(--ink);font-weight:700;margin-bottom:9px;line-height:1.3}
.ib-ar em{font-style:normal;font-family:Montserrat,sans-serif;font-size:9.5px;letter-spacing:.24em;
  color:var(--ac);font-weight:700;display:block;margin-bottom:16px}
.ib-ar span{font-size:13.5px;color:#5a6270;line-height:2}

.ib-cs{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.ib-c{background:#fff;border:1px solid #e4e7ec}
.ib-c .ib-ph{aspect-ratio:16/10}
.ib-cb{padding:16px 16px 18px}
.ib-ck{font-family:Montserrat,sans-serif;font-size:8.5px;letter-spacing:.16em;color:var(--ac);
  font-weight:700;margin:0}
.ib-cn{font-size:24px;font-weight:800;margin:5px 0 2px;letter-spacing:-.02em}
.ib-cn small{font-size:11px;font-weight:600;color:#9aa1ab;margin-left:4px}
.ib-c p{font-size:10.5px;color:#6b7280;line-height:1.8;margin:9px 0 0}

.ib-faq{border:none;padding:0;margin:0}
.ib-q{border-bottom:1px solid #e4e7ec;position:relative}
.ib-q:first-of-type{border-top:1px solid #e4e7ec}
.ib-q summary{padding:18px 42px 18px 0;font-size:13px;font-weight:500;cursor:pointer;list-style:none}
.ib-q summary::-webkit-details-marker{display:none}
.ib-q summary::before,.ib-q summary::after{content:'';position:absolute;right:8px;background:var(--ac);
  transition:transform .25s ease}
.ib-q summary::before{top:27px;width:13px;height:2px}
.ib-q summary::after{top:22px;right:13.5px;width:2px;height:13px}
.ib-q[open] summary::after{transform:scaleY(0)}
.ib-q .ans{font-size:12px;color:#6b7280;line-height:2;padding:0 42px 18px 0;margin:0}

.ib-ct{background:#2b2b2b;color:#fff;padding:60px 46px;text-align:center}
.ib-ct h2{font-family:Montserrat,sans-serif;font-size:38px;font-weight:800;margin:0 0 6px;
  letter-spacing:-.02em}
.ib-ct .ja{font-size:12px;color:#a8adb5;margin:0 0 26px}
.ib-ctb{display:flex;gap:12px;justify-content:center;flex-wrap:wrap}
.ib-ctb a{text-decoration:none;font-weight:700;font-size:13px;padding:15px 32px}
.ib-b1{background:var(--ac);color:#fff}
.ib-b2{border:1px solid rgba(255,255,255,.4);color:#fff}
.ib-tel{font-family:Montserrat,sans-serif;font-size:28px;font-weight:800;margin:26px 0 3px;
  color:#fff;text-decoration:none;display:inline-block}
.ib-hrs{font-size:10px;color:#a8adb5;margin:0}

/* ---- スクロールイン ----
   要素そのものは潰さない。潰すと IntersectionObserver が交差を検出できず、
   表示用クラスが永久に付かなくなる。覆いを疑似要素で被せて外す。 */
.rv-on [data-rv="up"]{opacity:0;transform:translateY(30px);
  transition:opacity .7s cubic-bezier(.22,.61,.36,1),transform .7s cubic-bezier(.22,.61,.36,1)}
.rv-on [data-rv="up"].is-in{opacity:1;transform:none}
.rv-on [data-rv="curtain"]{position:relative}
.rv-on [data-rv="curtain"]::after{content:'';position:absolute;inset:0;background:#fff;z-index:3;
  transform-origin:bottom;transform:scaleY(1);transition:transform .85s cubic-bezier(.22,.61,.36,1)}
.rv-on .gray [data-rv="curtain"]::after{background:#f6f7f9}
.rv-on [data-rv="curtain"].is-in::after{transform:scaleY(0)}

@media (prefers-reduced-motion: reduce){
  .rv-on [data-rv]{opacity:1!important;transform:none!important}
  .rv-on [data-rv]::after{display:none!important}
}

@media (max-width:1000px){
  .ib-split,.ib-nums{grid-template-columns:1fr;gap:26px}
  .ib-cs{grid-template-columns:1fr}
  .ib-s{padding:54px 24px}
  .ib-areasec .ib-sh h2{font-size:24px}
  .ib-ar{min-width:50%;padding:20px 12px}
  .ib-ar b{font-size:22px}
  .ib-ar+.ib-ar::before{display:none}
  .ib-areas{margin-top:30px}
  .ib-cat{right:20px;bottom:92px}
  .ib-anch{left:20px}
  .ib-crumb{left:20px;right:20px}
  .ib-big{left:18px}
}
`;

const InboundBusinessPage = () => {
  const { t, i18n } = useTranslation();
  const { lang, navigate, switchLang } = useLocalizedNav();
  useDocumentMeta('inbound');

  const rootRef = useRef(null);
  const ringRef = useRef(null);
  const ringLayerRef = useRef(null);
  const areaRef = useRef(null);

  useScrollReveal(rootRef);
  useParallax(rootRef);
  useCountUp(rootRef);
  useRingDecoration(ringRef, ringLayerRef, areaRef);
  useSideLabels(rootRef);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // 「事業」はホーム内の WORK セクションへ。共通ヘッダーと同じ挙動に揃える。
  const goToWork = () => {
    navigate('/');
    setTimeout(() => {
      const section = document.getElementById('work');
      if (section) section.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="ib" ref={rootRef} lang={i18n.language}>
      <style>{STYLES}</style>

      {/* 背景のリング。wrapの外側を通り、左右で見切れる */}
      <div className="ib-ringclip" aria-hidden="true">
        <div className="ib-ringlayer" ref={ringLayerRef}>
          <div className="ib-ring" ref={ringRef} />
        </div>
      </div>

      <div className="ib-side l" data-sidelabel aria-hidden="true">
        <span>{SIDE_LABELS.left}</span>
      </div>
      <div className="ib-side r" data-sidelabel aria-hidden="true">
        <span>{SIDE_LABELS.right}</span>
      </div>

      <div className="ib-wrap">
        <header className="ib-hd">
          <button type="button" className="logo" onClick={() => navigate('/')}
            aria-label={t('header.home')}>
            <img src={`${process.env.PUBLIC_URL}/jtclogo.png`} alt="株式会社J・T・C" />
          </button>
          <nav>
            <button type="button" onClick={() => navigate('/')}>{t('header.home')}</button>
            <button type="button" onClick={() => navigate('/news')}>{t('header.news')}</button>
            <button type="button" onClick={() => navigate('/about')}>{t('header.about')}</button>
            <button type="button" onClick={goToWork}>{t('header.work')}</button>
            <span className="ib-lang">
              <select value={lang} onChange={(e) => switchLang(e.target.value)}
                aria-label={t('header.languageLabel')}>
                {SUPPORTED_LANGS.map((code) => (
                  <option key={code} value={code}>{LANG_NAMES[code]}</option>
                ))}
              </select>
            </span>
          </nav>
          <a className="cta" href={`mailto:${CONTACT.email}`}>{t('header.contact')} →</a>
        </header>

        <div className="ib-hero">
          <div className="bg" data-para="0.3" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
          <div className="inner">
            <p className="ib-crumb">
              <span aria-current="page">{t('inbound.title')}</span>
            </p>
            <h1 className="ib-big" data-para="-0.14">INBOUND<br />TRAVEL</h1>
            <p className="ib-cat" data-para="-0.06">
              {t('inbound.catch1')}<br />{t('inbound.catch2')}
            </p>
            <div className="ib-anch">
              <a href="#ib-services" onClick={scrollTo('ib-services')}>{t('inbound.services.title')} ↓</a>
              <a href="#ib-cases" onClick={scrollTo('ib-cases')}>{t('inbound.cases.title')} ↓</a>
            </div>
          </div>
        </div>

        {/* 導入 */}
        <section className="ib-s">
          <div className="ib-split">
            <div>
              <h2 className="ib-h3" data-rv="up">
                {t('inbound.lead.head1')}<br />
                <em>{t('inbound.lead.headEm')}</em>{t('inbound.lead.head2')}
              </h2>
              <p className="ib-p" data-rv="up">{t('inbound.lead.body')}</p>
            </div>
            <div className="ib-ph" data-rv="curtain">
              <div className="img" data-para="0.1" style={{ backgroundImage: `url(${IMAGES.intro})` }} />
            </div>
          </div>
        </section>

        {/* 手配できること */}
        <section className="ib-s gray" id="ib-services">
          <div className="ib-sh" data-rv="up">
            <h2>{t('inbound.services.title')}</h2>
            <p>SERVICES</p>
          </div>
          <div className="ib-split">
            <div className="ib-ph" data-rv="curtain">
              <div className="img" data-para="0.1" style={{ backgroundImage: `url(${IMAGES.services})` }} />
            </div>
            <div>
              <h3 className="ib-h3" data-rv="up">
                {t('inbound.services.head1')}<br />
                <em>{t('inbound.services.headEm')}</em>{t('inbound.services.head2')}
              </h3>
              <ul className="ib-ul">
                {SERVICE_KEYS.map((key) => (
                  <li key={key} data-rv="up">
                    {t(`inbound.services.items.${key}.name`)} — {t(`inbound.services.items.${key}.desc`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 数字 */}
        <section className="ib-nums">
          <div className="ib-n">
            <p className="ib-nl" data-rv="up">{t('inbound.figures.licence.label')}</p>
            <p className="ib-nb" data-rv="up">
              {t('inbound.figures.licence.prefix')}{' '}
              <small>
                {/* 登録番号の書き方は言語ごとに違う（第◯号 / No. ◯）。
                    数え上げる数字だけを差し込み、前後は翻訳側に任せる。 */}
                <Trans
                  i18nKey="inbound.figures.licence.number"
                  components={{ n: <span data-count-to="7598" /> }}
                />
              </small>
            </p>
            <p className="ib-nn" data-rv="up">{t('inbound.figures.licence.note')}</p>
          </div>
          <div className="ib-n">
            <p className="ib-nl" data-rv="up">{t('inbound.figures.size.label')}</p>
            <p className="ib-nb" data-rv="up">
              <span data-count-to="10">10</span>
              <small>{t('inbound.figures.size.unit')}</small>
            </p>
            <p className="ib-nn" data-rv="up">{t('inbound.figures.size.note')}</p>
          </div>
        </section>

        {/* 対応エリア。背景を透過させ、リングが背後を通る */}
        <section className="ib-s clear ib-areasec" ref={areaRef}
          style={{ paddingTop: 132, paddingBottom: 132, textAlign: 'center' }}>
          <div className="ib-sh" data-rv="up" style={{ marginBottom: 22 }}>
            <h2>{t('inbound.areas.title')}</h2>
            <p>AREAS</p>
          </div>
          <p className="lead" data-rv="up">{t('inbound.areas.lead')}</p>
          <div className="ib-areas">
            {AREA_KEYS.map((key) => (
              <div className="ib-ar" key={key} data-rv="up">
                <b>{t(`inbound.areas.items.${key}.name`)}</b>
                <em>{t(`inbound.areas.items.${key}.en`)}</em>
                <span>{t(`inbound.areas.items.${key}.cities`)}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 手配事例 */}
        <section className="ib-s gray" id="ib-cases">
          <div className="ib-sh" data-rv="up">
            <h2>{t('inbound.cases.title')}</h2>
            <p>CASES</p>
          </div>
          <div className="ib-cs">
            {CASES.map((c) => (
              <article className="ib-c" key={c.id} data-rv="up">
                <div className="ib-ph">
                  <div className="img" data-para="0.07" style={{ backgroundImage: `url(${c.image})` }} />
                </div>
                <div className="ib-cb">
                  <p className="ib-ck">{c.kind}</p>
                  <p className="ib-cn">
                    {pick(c.figure, lang)}<small>{pick(c.unit, lang)}</small>
                  </p>
                  <p>{pick(c.text, lang)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="ib-s">
          <div className="ib-sh" data-rv="up">
            <h2>{t('inbound.faq.title')}</h2>
            <p>FAQ</p>
          </div>
          {FAQ_KEYS.map((key) => (
            <details className="ib-q" key={key} data-rv="up">
              <summary>{t(`inbound.faq.items.${key}.q`)}</summary>
              <p className="ans">{t(`inbound.faq.items.${key}.a`)}</p>
            </details>
          ))}
        </section>

        <section className="ib-ct">
          <h2 data-rv="up">CONTACT</h2>
          <p className="ja" data-rv="up">{t('inbound.contact.lead')}</p>
          <div className="ib-ctb" data-rv="up">
            <a className="ib-b1" href={`mailto:${CONTACT.email}`}>{t('inbound.contact.mail')}</a>
            <a className="ib-b2" href={`${process.env.PUBLIC_URL}/about`}
              onClick={(e) => { e.preventDefault(); navigate('/about'); }}>
              {t('inbound.contact.about')}
            </a>
          </div>
          <a className="ib-tel" href={CONTACT.telHref} data-rv="up">{CONTACT.tel}</a>
          <p className="ib-hrs" data-rv="up">{t('inbound.contact.hours')}</p>
        </section>
      </div>
    </div>
  );
};

export default InboundBusinessPage;
